// Pure interpretation of the existing probe reports. Unknown or incomplete
// evidence must never become a pass, nor a claim that a header is missing.
// Every sentence is a sec-js-* message in the page's language; a probe's own
// sentence arrives in English and keeps that `lang` on its finding.
import { t } from "./_i18n.js";

export function targetURLs(raw) {
    const value = raw.trim();
    if (!value || value.length > 2048) throw new Error(t("sec-js-url-empty"));
    let url;
    try { url = new URL(value.includes("://") ? value : `https://${value}`); }
    catch { throw new Error(t("sec-js-url-invalid")); }
    if (!["http:", "https:"].includes(url.protocol) || url.username || url.password || url.port) {
        throw new Error(t("sec-js-url-scheme"));
    }
    const host = url.hostname.replace(/\.$/, "");
    if (!host.includes(".") || /^[\d.]+$/.test(host) || host.includes(":") ||
        !host.split(".").every(label => /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/i.test(label))) {
        throw new Error(t("sec-js-url-public"));
    }
    url.hostname = host;
    url.hash = "";
    url.protocol = "https:";
    const https = url.href;
    url.protocol = "http:";
    return { host, https, http: url.href };
}

export function validReport(kind, r) {
    if (!r || r.ok !== true) return false;
    if (kind === "ssl") return typeof r.host === "string" && Number.isFinite(r.days_remaining) &&
        typeof r.expired === "boolean" && typeof r.name_matches === "boolean" &&
        Number.isFinite(Date.parse(r.not_before)) && Number.isFinite(Date.parse(r.not_after)) &&
        Number.isInteger(r.chain_len) && r.chain_len > 0 && typeof r.self_signed === "boolean";
    const webURL = value => {
        try { return ["http:", "https:"].includes(new URL(value).protocol); } catch { return false; }
    };
    return webURL(r.final_url) && Number.isInteger(r.final_status) && Number.isInteger(r.total_ms) &&
        typeof r.redirect_loop === "boolean" && typeof r.hop_limit_hit === "boolean" &&
        typeof r.headers_truncated === "boolean" && Array.isArray(r.headers) &&
        r.headers.every(pair => Array.isArray(pair) && pair.length === 2 && pair.every(v => typeof v === "string")) &&
        Array.isArray(r.hops) && r.hops.length > 0 &&
        r.hops.every(h => webURL(h.url) && Number.isInteger(h.status) && Number.isInteger(h.ms));
}

// `lang` names the language of `evidence` when it is not the page's.
const finding = (id, status, title, evidence, advice, lang) => ({ id, status, title, evidence, advice, lang });
const unknown = (id, title, evidence, lang) => finding(id, "unknown", title, evidence, t("sec-js-advice-retry"), lang);
// The redirect trouble a chain's evidence line ends with; `ends` also names
// a last hop that redirected nowhere.
const trouble = (r, ends = false) => [
    r.redirect_loop && t("sec-js-trouble-loop"),
    r.hop_limit_hit && t("sec-js-trouble-limit"),
    ends && dangling(r) && t("sec-js-trouble-dangling"),
].filter(Boolean).map(s => ` · ${s}`).join("");
const complete = r => r && !r.redirect_loop && !r.hop_limit_hit;
// The probe ends a walk on a 3xx that carries no Location: a browser shows nothing there.
const dangling = r => r.final_status >= 300 && r.final_status < 400;
const secure = url => new URL(url).protocol === "https:";
const downgraded = r => r.hops.some((h, i) => i > 0 && secure(r.hops[i - 1].url) && !secure(h.url));

// Values cut by the header probe end in an ellipsis. Multiple policies and
// duplicate values need browser-specific interpretation, so defer them.
function header(r, name) {
    if (!complete(r)) return { unknown: t("sec-js-unknown-chain") };
    const values = r.headers.filter(([key]) => key.toLowerCase() === name).map(([, value]) => value);
    if (r.headers_truncated || values.some(v => v.endsWith("…") || v === "<not text>")) {
        return { unknown: t("sec-js-unknown-truncated") };
    }
    if (values.length > 1) return { unknown: t("sec-js-unknown-multiple") };
    return { value: values[0] ?? null };
}

function directives(value) {
    const map = new Map();
    for (const piece of value.split(";")) {
        const [name, ...tokens] = piece.trim().split(/\s+/);
        const key = name.toLowerCase();
        // CSP uses the first occurrence; keywords and schemes match case-insensitively.
        if (key && !map.has(key)) map.set(key, tokens.map(token => token.toLowerCase()));
    }
    return map;
}

// `reason` is why the HTTPS walk has no report, in `lang`.
function headerFindings(r, reason, lang) {
    const specs = [
        ["hsts", t("sec-js-title-hsts"), "strict-transport-security"],
        ["csp", t("sec-js-title-csp"), "content-security-policy"],
        ["framing", t("sec-js-title-framing"), "x-frame-options"],
        ["nosniff", t("sec-js-title-nosniff"), "x-content-type-options"],
        ["referrer", t("sec-js-title-referrer"), "referrer-policy"],
    ];
    if (!complete(r) || !secure(r.final_url) || dangling(r)) return specs.map(([id, title]) => reason ?
        unknown(id, title, reason, lang) :
        unknown(id, title, t(r && dangling(r) ? "sec-js-unknown-dangling" : "sec-js-unknown-no-response")));
    const csp = header(r, "content-security-policy");
    return specs.map(([id, title, name]) => {
        const h = header(r, name);
        if (h.unknown) return unknown(id, title, h.unknown);
        const raw = h.value;
        const evidence = raw === null ? t("sec-js-evidence-absent", { name }) : `${name}: ${raw}`;
        const result = (status, advice, detail = evidence) => finding(id, status, title, detail, advice);
        if (id === "hsts") {
            const parts = raw?.split(";").map(s => s.trim()) ?? [];
            const ages = parts.filter(s => /^max-age\s*=/i.test(s));
            const age = ages.length === 1 ? /^max-age\s*=\s*(?:"(\d+)"|(\d+))$/i.exec(ages[0]) : null;
            const seconds = age ? Number(age[1] ?? age[2]) : 0;
            if (!age || !Number.isFinite(seconds) || seconds === 0 || raw.includes(",")) return result("warning", t("sec-js-advice-hsts-missing"));
            if (seconds < 15552000) return result("warning", t("sec-js-advice-hsts-short"));
            return result("pass", t("sec-js-advice-hsts-pass"));
        }
        if (id === "csp") {
            if (!raw) return result("warning", t("sec-js-advice-csp-missing"));
            if (raw.includes(",")) return unknown(id, title, t("sec-js-unknown-csp-combined"));
            const policy = directives(raw);
            const scripts = policy.get("script-src-elem") ?? policy.get("script-src") ?? policy.get("default-src");
            const general = policy.get("script-src") ?? policy.get("default-src");
            // Under 'strict-dynamic' with a nonce or hash, browsers ignore host,
            // scheme and 'unsafe-inline' sources; they are the compatibility fallback.
            const strict = list => list.includes("'strict-dynamic'") && list.some(s => /^'(nonce|sha(256|384|512))-/.test(s));
            const effective = list => strict(list) ? list.filter(s => s.startsWith("'") && s !== "'unsafe-inline'") : list;
            const risky = ["*", "http:", "https:", "data:", "'unsafe-inline'", "'unsafe-eval'"];
            if (!scripts || !general || [...effective(scripts), ...effective(general)].some(s => risky.includes(s) || s.startsWith("http://") || s.includes("*")) ||
                policy.get("script-src-attr")?.includes("'unsafe-inline'") ||
                (policy.get("object-src") ?? policy.get("default-src"))?.join(" ") !== "'none'" ||
                !["'none'", "'self'"].includes(policy.get("base-uri")?.join(" "))) {
                return result("warning", t("sec-js-advice-csp-review"));
            }
            return result("pass", t("sec-js-advice-csp-pass"));
        }
        if (id === "framing") {
            if (csp.unknown || csp.value?.includes(",")) return unknown(id, title, csp.unknown || t("sec-js-unknown-framing-combined"));
            const ancestors = csp.value ? directives(csp.value).get("frame-ancestors") : undefined;
            if (ancestors) {
                // A host source may omit the scheme and lead with a wildcard label; a bare
                // scheme or a lone wildcard is not a restriction.
                const origin = /^(?:https:\/\/)?(?:\*\.)?[a-z0-9-]+(?:\.[a-z0-9-]+)*(?::\d+)?$/;
                const restricted = ancestors.length > 0 && (ancestors.join(" ") === "'none'" ||
                    ancestors.every(s => s === "'self'" || origin.test(s)));
                return result(restricted ? "pass" : "warning",
                    t(restricted ? "sec-js-advice-ancestors-pass" : "sec-js-advice-ancestors-review"),
                    `content-security-policy frame-ancestors: ${ancestors.join(" ")}`);
            }
            const xfo = raw && /^(DENY|SAMEORIGIN)$/i.test(raw.trim());
            return result(xfo ? "pass" : "warning", t(xfo ? "sec-js-advice-xfo-pass" : "sec-js-advice-framing-missing"));
        }
        if (id === "nosniff") {
            const nosniff = raw?.trim().toLowerCase() === "nosniff";
            return result(nosniff ? "pass" : "warning", t(nosniff ? "sec-js-advice-nosniff-pass" : "sec-js-advice-nosniff-missing"));
        }
        const recognized = ["no-referrer", "no-referrer-when-downgrade", "origin", "origin-when-cross-origin", "same-origin", "strict-origin", "strict-origin-when-cross-origin", "unsafe-url"];
        const tokens = (raw ?? "").split(",").map(s => s.trim());
        const effective = tokens.filter(s => recognized.includes(s)).at(-1);
        const preferred = ["no-referrer", "same-origin", "strict-origin", "strict-origin-when-cross-origin"].includes(effective);
        return result(preferred ? "pass" : "warning", preferred ?
            t("sec-js-advice-referrer-pass", { policy: effective }) : t("sec-js-advice-referrer-missing"));
    });
}

export function assess(target, ssl, https, http, now = Date.now()) {
    const checks = [];
    const cert = ssl.report;
    if (cert) {
        const notYet = Date.parse(cert.not_before) > now;
        checks.push(finding("certificate", cert.expired || notYet ? "fail" : cert.days_remaining <= 30 ? "warning" : "pass",
            t("sec-js-title-certificate"),
            cert.days_remaining < 0 ?
                t("sec-js-evidence-dates-ago", { host: target.host, from: cert.not_before, to: cert.not_after, count: -cert.days_remaining }) :
                t("sec-js-evidence-dates", { host: target.host, from: cert.not_before, to: cert.not_after, count: cert.days_remaining }),
            t(cert.expired ? "sec-js-advice-cert-expired" : notYet ? "sec-js-advice-cert-not-yet" :
                cert.days_remaining <= 30 ? "sec-js-advice-cert-soon" : "sec-js-advice-cert-pass")));
        checks.push(finding("hostname", cert.name_matches ? "pass" : "fail", t("sec-js-title-hostname"),
            t(cert.name_matches ? "sec-js-evidence-host-covered" : "sec-js-evidence-host-mismatch", { host: target.host }),
            t(cert.name_matches ? "sec-js-advice-host-pass" : "sec-js-advice-host-mismatch")));
        if (cert.self_signed) checks.push(finding("self-issued", "warning", t("sec-js-title-self-issued"),
            t("sec-js-evidence-self-issued"), t("sec-js-advice-self-issued")));
    } else {
        checks.push(unknown("certificate", t("sec-js-title-cert-and-host"), ssl.error, ssl.lang));
    }
    const web = https.report;
    if (web) {
        checks.push(finding("trust", "pass", t("sec-js-title-trust"),
            t("sec-js-evidence-trust", { url: target.https }),
            [
                t("sec-js-advice-trust-accepted"),
                cert ? t("sec-js-advice-trust-chain", { count: cert.chain_len }) : t("sec-js-advice-trust-no-cert"),
                t("sec-js-advice-trust-untested"),
            ].join(" ")));
        const bad = downgraded(web) || !secure(web.final_url);
        checks.push(finding("https", bad || !complete(web) || dangling(web) ? "fail" : web.final_status >= 400 ? "warning" : "pass",
            t("sec-js-title-https"),
            t("sec-js-evidence-status-at", { status: web.final_status, url: web.final_url }) + trouble(web, true),
            t(bad ? "sec-js-advice-https-downgrade" : !complete(web) ? "sec-js-advice-https-incomplete" :
                dangling(web) ? "sec-js-advice-https-dangling" : web.final_status >= 400 ? "sec-js-advice-https-error" :
                    "sec-js-advice-https-pass")));
    } else {
        // The header probe validates certificates; its TLS sentence is a verdict, not a gap.
        // It does not say which hop failed, so the advice covers the whole chain.
        checks.push(/TLS handshake/.test(https.error ?? "") ?
            finding("trust", "fail", t("sec-js-title-trust"), `${target.https}: ${https.error}`,
                t("sec-js-advice-trust-fail"), https.lang) :
            unknown("trust", t("sec-js-title-trust"), https.error, https.lang));
        checks.push(unknown("https", t("sec-js-title-https"), https.error, https.lang));
    }
    const plain = http.report;
    if (plain) {
        const upgrade = complete(plain) && secure(plain.final_url) && !downgraded(plain);
        checks.push(finding("http", upgrade ? "pass" : "fail", t("sec-js-title-http"),
            `${target.http} → ${plain.final_url}${trouble(plain)}`,
            t(upgrade ? "sec-js-advice-http-pass" : "sec-js-advice-http-fail")));
    } else checks.push(unknown("http", t("sec-js-title-http"), http.error, http.lang));
    checks.push(...headerFindings(web, https.error, https.lang));
    const order = { fail: 0, warning: 1, unknown: 2, pass: 3 };
    return checks.sort((a, b) => order[a.status] - order[b.status]);
}
